import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Edit, Delete, FindReplace } from '@mui/icons-material';
import { IHandbookDocument } from 'core/interfaces/handbookInterface';
import styles from '../SchedulerSetting.module.scss';

interface HandbookDocumentsListProps {
  documents: IHandbookDocument[];
  onRenameClick: (document: IHandbookDocument) => void;
  onReplaceClick: (document: IHandbookDocument) => void;
  onDeleteClick: (document: IHandbookDocument) => void;
}

const HandbookDocumentsList: React.FC<HandbookDocumentsListProps> = ({
  documents,
  onRenameClick,
  onReplaceClick,
  onDeleteClick,
}) => {
  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (documents.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No employee handbook documents uploaded yet.</p>
        <p>Click &quot;Upload Handbook Document&quot; to add a PDF.</p>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.workplaceTable}>
        <thead>
          <tr>
            <th>Document Name</th>
            <th style={{ width: 80, textAlign: 'center' }}>Version</th>
            <th style={{ width: 160 }}>Last Updated</th>
            <th style={{ width: 120, textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {documents.map((doc) => (
            <tr key={doc.id}>
              <td>
                <strong>{doc.name}</strong>
              </td>
              <td style={{ textAlign: 'center' }}>
                <span>v{doc.version ?? 1}</span>
              </td>
              <td>
                <span>{formatDate(doc.updatedAt || doc.createdAt)}</span>
              </td>
              <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                <Tooltip title="Rename">
                  <IconButton
                    size="small"
                    onClick={() => onRenameClick(doc)}
                    className={styles.iconButton}
                    aria-label={`Rename ${doc.name}`}
                  >
                    <Edit className={styles.editIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Replace file">
                  <IconButton
                    size="small"
                    onClick={() => onReplaceClick(doc)}
                    className={styles.iconButton}
                    aria-label={`Replace ${doc.name}`}
                  >
                    <FindReplace className={styles.editIcon} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => onDeleteClick(doc)}
                    className={styles.iconButton}
                    color="error"
                    aria-label={`Delete ${doc.name}`}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default HandbookDocumentsList;
